/**
 * 总览：设备激活与设备密钥签发的整体态势。
 *
 * 数据来自 `/api/devices`（取前 200 台）与 `/api/health`。
 * 这里只做只读汇总，任何写操作都在设备详情页完成。
 */
import {
  Clock,
  KeyRound,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Smartphone,
  TriangleAlert,
} from 'lucide-react'
import { api, useAsync } from '@/lib/api'
import { cn, formatTime } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'

/**
 * 按动作前缀着色：设备密钥类动作用 warning（最需要盯），
 * 仪器激活码类用主色，状态变更用 success，维护动态密码用 outline，管理类用 destructive。
 */
function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  tone,
}: {
  label: string
  value: number | null
  hint?: string
  icon: typeof Smartphone
  tone: string
}) {
  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-3 p-5">
        <div className="min-w-0 space-y-1">
          <div className="text-xs text-[var(--color-muted-foreground)]">{label}</div>
          <div className="text-2xl font-semibold tabular-nums">
            {value === null ? (
              <Loader2 className="size-5 animate-spin text-[var(--color-muted-foreground)]" />
            ) : (
              value
            )}
          </div>
          {hint ? (
            <div className="text-[11px] text-[var(--color-muted-foreground)]">{hint}</div>
          ) : null}
        </div>
        <div className={cn('grid size-9 shrink-0 place-items-center rounded-md', tone)}>
          <Icon className="size-4.5" />
        </div>
      </CardContent>
    </Card>
  )
}

function InlineError({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-[var(--color-destructive)] p-3 text-xs text-[var(--color-destructive)]">
      <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
      <span>加载失败：{text}</span>
    </div>
  )
}

export function DashboardPage() {
  const devicesState = useAsync(() => api.listDevices({ pageSize: 200 }), [])
  const healthState = useAsync(() => api.health(), [])

  const rows = devicesState.data?.rows ?? []
  const total = devicesState.data?.total ?? rows.length
  const activeCount = rows.filter((d) => d.status === 'active').length
  const pendingDevices = rows.filter((d) => d.status === 'pending')
  const secretCount = rows.filter((d) => Boolean(d.has_secret)).length
  const coverageHint = total > rows.length ? `仅统计最近 ${rows.length} 台` : undefined

  const busy = devicesState.loading

  function reloadAll() {
    devicesState.reload()
    healthState.reload()
  }

  const healthNote = healthState.data
    ? `服务端时间 ${formatTime(healthState.data.time)}`
    : healthState.error
      ? '服务端状态不可用'
      : '正在读取服务端状态…'

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">总览</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">{healthNote}</p>
        </div>
        <Button variant="outline" size="sm" onClick={reloadAll} disabled={busy}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />}
          刷新
        </Button>
      </div>

      {healthState.data && !healthState.data.masterKeyConfigured ? (
        <div className="flex items-start gap-2.5 rounded-lg border border-[var(--color-warning)] bg-[color-mix(in_oklch,var(--color-warning)_12%,transparent)] p-3.5 text-sm">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-[var(--color-warning)]" />
          <div className="space-y-0.5">
            <div className="font-medium text-[var(--color-warning)]">主密钥未配置</div>
            <p className="text-xs text-[var(--color-muted-foreground)]">
              设备密钥签发、仪器激活码与维护动态密码都依赖主密钥。请配置环境变量{' '}
              <code>DSKGI_MASTER_KEY</code>
              （32 字节 hex 或 base64）后重启管理端服务。
            </p>
          </div>
        </div>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="设备总数"
          value={devicesState.data ? total : null}
          icon={Smartphone}
          tone="bg-[color-mix(in_oklch,var(--color-primary)_18%,transparent)] text-[var(--color-primary)]"
        />
        <StatCard
          label="已激活"
          value={devicesState.data ? activeCount : null}
          hint={coverageHint}
          icon={ShieldCheck}
          tone="bg-[color-mix(in_oklch,var(--color-success)_18%,transparent)] text-[var(--color-success)]"
        />
        <StatCard
          label="待批准"
          value={devicesState.data ? pendingDevices.length : null}
          hint={coverageHint}
          icon={Clock}
          tone="bg-[color-mix(in_oklch,var(--color-warning)_18%,transparent)] text-[var(--color-warning)]"
        />
        <StatCard
          label="已签发设备密钥"
          value={devicesState.data ? secretCount : null}
          hint={coverageHint}
          icon={KeyRound}
          tone="bg-[color-mix(in_oklch,var(--color-primary)_18%,transparent)] text-[var(--color-primary)]"
        />
      </div>

      {devicesState.error ? <InlineError text={devicesState.error} /> : null}
    </div>
  )
}
